/**
 * The two documents the payment page puts in a 3-D Secure iframe.
 *
 * Both are documents WE write: a form that posts itself to the bank. An
 * iframe `srcdoc` runs in this page's own origin, so nothing the gateway
 * sent is ever executed there — its values are HTML-escaped into our form.
 */

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"'`]/g, (c) => `&#${c.charCodeAt(0)};`);

/** Only an absolute http(s) URL may be a form target. */
export function safeHttpUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * A document that posts `fields` to `action` as soon as it loads.
 *
 * The page itself asks for no Referer at all (see pay/payLinkToken.js). A
 * srcdoc frame inherits that, which would make the bank see `Origin: null`
 * on this POST; the frame states `origin` so the bank sees the app's origin
 * (and nothing else — never a path, never the fragment).
 */
export function buildAutoPostDocument(action, fields, { message = 'Contacting your bank…' } = {}) {
  const target = safeHttpUrl(action);
  if (!target) return null;
  const inputs = fields
    .filter(([name, value]) => name && value != null && value !== '')
    .map(([name, value]) => `<input type="hidden" name="${escapeHtml(name)}" value="${escapeHtml(value)}">`)
    .join('');
  return '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">'
    + '<meta name="referrer" content="origin">'
    + '<meta name="viewport" content="width=device-width, initial-scale=1">'
    + '<title>Card verification</title></head>'
    + '<body style="margin:16px;font-family:system-ui,sans-serif;font-size:16px;">'
    + `<p>${escapeHtml(message)}</p>`
    + `<form id="f" method="POST" action="${escapeHtml(target)}">${inputs}`
    + '<noscript><button type="submit">Continue</button></noscript></form>'
    + '<script>document.getElementById("f").submit();</script>'
    + '</body></html>';
}

/** The challenge: post the challenge request to the bank's access control server. */
export function buildChallengeDocument({ acsURL, cReq, threeDSSessionData } = {}) {
  if (!cReq) return null;
  return buildAutoPostDocument(acsURL, [
    ['creq', cReq],
    ['threeDSSessionData', threeDSSessionData],
  ]);
}

/**
 * The method step. The gateway sends a ready-made form (`methodForm`); it
 * is parsed inertly — DOMParser runs no script — and its target and hidden
 * fields are re-emitted in our own document.
 *
 * @returns {{html: string, sandboxed: boolean} | null} `sandboxed` when the
 *   gateway's markup held no form we could read: it is then shown as it
 *   came, in a frame with no access to this page's origin.
 */
export function buildMethodDocument(methodForm) {
  if (typeof methodForm !== 'string' || !methodForm.trim()) return null;
  let form = null;
  try {
    form = new DOMParser().parseFromString(methodForm, 'text/html').querySelector('form[action]');
  } catch {
    form = null;
  }
  if (form) {
    const fields = Array.from(form.querySelectorAll('input[name]'))
      .map((input) => [input.getAttribute('name'), input.getAttribute('value') ?? '']);
    const html = buildAutoPostDocument(form.getAttribute('action'), fields);
    if (html) return { html, sandboxed: false };
  }
  return { html: methodForm, sandboxed: true };
}
